import React, { useState } from 'react';
import { Card, Checkbox, Row, Space, Divider, Menu, Dropdown } from 'antd';
import styled from 'styled-components';

import { ReactComponent as DownloadOutlined } from '../../../images/downloaded-outline.svg';
import { ReactComponent as DeleteOutlined } from '../../../images/delete-outline.svg';
import { ReactComponent as MoreAction } from '../../../images/more-vert.svg';
import { GlossaryNode, GlossaryTerm } from '../../../types.generated';
import { Notification } from '../../shared/Notification';
import { Message } from '../../shared/Message';

import { ChildGlossaryTermFragment } from '../../../graphql/glossaryNode.generated';
import { useExportEntityMutation } from '../../../graphql/mutations.generated';
import { GlossaryNodeFragment } from '../../../graphql/fragments.generated';
import { useUserContext } from '../../context/useUserContext';
import { useTranslation } from '../../../i18n/useTranslation';

const GREY_COLOR = '#5F767C';

const styles = {
    row: { padding: '12px' },
};

const BrowseDropDown = styled(Dropdown)`
    display: flex;
    align-items: center;
    cursor: pointer;
`;

const BrowseMenu = styled(Menu)`
    padding: 0px;
    border-radius: 4px;
    min-width: 140px !important;
`;

interface Props {
    nodes: (GlossaryNode | GlossaryNodeFragment)[];
    terms: (GlossaryTerm | ChildGlossaryTermFragment)[];
    selectedItems: string[];
    isDeleting?: boolean;
    isAllSelected: () => boolean;
    handleBulkDelete?: () => void;
    isIndeterminate: () => boolean;
    handleSelectAll: (event) => void;
    setSelectedItems: (event) => void;
}

function GlossaryActions({
    nodes,
    terms,
    isDeleting,
    selectedItems,
    isAllSelected,
    setSelectedItems,
    handleSelectAll,
    isIndeterminate,
    handleBulkDelete,
}: Props) {
    const { t } = useTranslation();
    const [isCsvExporting, setIsCsvExporting] = useState<boolean>(false);
    const [exportEntity, setExportEntity]: any = useState(null);
    const [exportEntityMutation] = useExportEntityMutation();
    const user = useUserContext();

    const canManageGlossaries = user?.platformPrivileges?.manageGlossaries;

    /**
     * Handles the export of the selected datasets to a CSV file.
     * Calls the exportEntityMutation to export the selected datasets to a CSV file.
     * Displays a notification message to the user once the export is completed.
     * Clears the selectedItems state and sets the isAllSelected state to false.
     */
    const exportCsv = () => {
        const glossaryNodeUrns = nodes.filter((item) => selectedItems.includes(item?.urn)).map((item) => item?.urn);
        const glossaryTermUrns = terms.filter((item) => selectedItems.includes(item?.urn)).map((item) => item?.urn);

        setIsCsvExporting(true);
        setExportEntity(null);

        const input = {
            glossaryTerm: {
                glossaryNodeUrns,
                glossaryTermUrns,
            },
            entityType: 'glossaryTerm',
        };

        exportEntityMutation({
            variables: {
                input,
            },
        })
            .then((res = {}) => {
                const { data } = res;
                setExportEntity(data?.exportEntity);
                setSelectedItems([]);
            })
            .catch((e) => {
                if (e.networkError.statusCode === 503) {
                    const updatedInput = {
                        ...input,
                        async: true,
                    };
                    // Retry with a different input for 504 errors
                    exportEntityMutation({
                        variables: {
                            input: updatedInput,
                        },
                    }).then((retryRes) => {
                        const { data } = retryRes;
                        setExportEntity(data?.exportEntity);
                        setSelectedItems([]);
                    });
                }
            })
            .finally(() => setIsCsvExporting(false));
    };

    return (
        <>
            {exportEntity && <Notification exportEntity={exportEntity} />}

            {isCsvExporting && <Message type="loading" content="Loading..." style={{ marginTop: '10%' }} />}
            {isDeleting && (
                <Message type="loading" content="Deleting glossary entities..." style={{ marginTop: '10%' }} />
            )}

            <Card>
                <Row style={styles.row} justify="space-between" align="middle">
                    <Space size="middle" align="center">
                        <Checkbox
                            checked={isAllSelected()}
                            indeterminate={isIndeterminate()}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                        >
                            {t('Select All')}
                        </Checkbox>
                    </Space>
                    <Row align="middle">
                        <BrowseDropDown
                            overlay={
                                <BrowseMenu>
                                    <Menu.Item
                                        key="1"
                                        onClick={exportCsv}
                                        disabled={selectedItems.length === 0}
                                        className="f-color-gray-s100"
                                        icon={<DownloadOutlined style={{ width: 16 }} color="#5F767C" />}
                                    >
                                        {t('Export')}
                                    </Menu.Item>
                                    <Menu.Item
                                        key="2"
                                        tabIndex={0}
                                        className="f-color-gray-s100"
                                        disabled={selectedItems.length === 0 || !canManageGlossaries}
                                        icon={<DeleteOutlined style={{ width: 14 }} color="#5F767C" />}
                                        onClick={handleBulkDelete}
                                    >
                                        {t('Delete')}
                                    </Menu.Item>
                                </BrowseMenu>
                            }
                            trigger={['click']}
                        >
                            <Row align="middle" style={{ gap: 8 }}>
                                <MoreAction
                                    data-testid="entity-header-dropdown"
                                    width={12}
                                    height={12}
                                    color={GREY_COLOR}
                                />
                                <span className="f-color-gray-s100">{t('Actions')}</span>
                            </Row>
                        </BrowseDropDown>
                    </Row>
                </Row>
                <Divider className="ma-0" />
            </Card>
        </>
    );
}

export default GlossaryActions;
